import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { TeamInput, TeamRecord } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { useArchiveTeam, useCreateTeam, useTeams, useUpdateTeam } from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Sheet, SheetNumber, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';

const blank: TeamInput = {
  name: '',
  ageCategory: undefined,
  gender: undefined,
  baseFormation: undefined,
  notes: undefined,
};

export function Teams({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const create = useCreateTeam();
  const update = useUpdateTeam();
  const archive = useArchiveTeam();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editing, setEditing] = useState<TeamRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<TeamInput>(blank);

  const set = <K extends keyof TeamInput>(k: K) => (value: TeamInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const startCreate = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const startEdit = (tm: TeamRecord) => {
    setEditing(tm);
    setForm({
      name: tm.name,
      ageCategory: tm.ageCategory ?? undefined,
      gender: (tm.gender as TeamInput['gender']) ?? undefined,
      baseFormation: tm.baseFormation ?? undefined,
      notes: tm.notes ?? undefined,
    });
    setOpen(true);
  };

  const list = teams.data ?? [];

  return (
    <>
      <PageHeader
        title={t('teams.title')}
        count={list.length || undefined}
        lead={t('teams.lead')}
        help={localePath(locale, '/guide/club')}
        action={
          <Button variant="primary" onClick={startCreate}>
            {t('teams.create')}
          </Button>
        }
      />

      {open ? (
        <div className="mb-5">
          <Panel title={editing ? t('teams.edit') : t('teams.create')}>
            <form
              noValidate
              className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  if (editing) await update.mutateAsync({ teamId: editing.id, input: form });
                  else await create.mutateAsync(form);
                });
                if (ok) {
                  setOpen(false);
                  setEditing(null);
                }
              }}
            >
              {formError ? (
                <div className="sm:col-span-2">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Field label={t('teams.name')} error={fieldError('name')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.name}
                    onChange={(e) => set('name')(e.target.value)}
                    required
                    autoFocus
                  />
                )}
              </Field>

              <Field
                label={t('teams.ageCategory')}
                hint={t('teams.ageCategoryHint')}
                optional
                error={fieldError('ageCategory')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.ageCategory ?? ''}
                    onChange={(e) => set('ageCategory')(e.target.value || undefined)}
                    placeholder="U19"
                  />
                )}
              </Field>

              <Field label={t('teams.gender')} optional error={fieldError('gender')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.gender ?? ''}
                    onChange={(e) =>
                      set('gender')((e.target.value || undefined) as TeamInput['gender'])
                    }
                  >
                    <option value="">{t('squad.none')}</option>
                    <option value="male">{t('teams.gender_male')}</option>
                    <option value="female">{t('teams.gender_female')}</option>
                    <option value="mixed">{t('teams.gender_mixed')}</option>
                  </SelectInput>
                )}
              </Field>

              <Field
                label={t('teams.baseFormation')}
                hint={t('teams.baseFormationHint')}
                optional
                error={fieldError('baseFormation')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.baseFormation ?? ''}
                    onChange={(e) => set('baseFormation')(e.target.value || undefined)}
                    placeholder="4-2-3-1"
                  />
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label={t('teams.notes')} optional error={fieldError('notes')}>
                  {(p) => (
                    <TextArea
                      {...p}
                      value={form.notes ?? ''}
                      onChange={(e) => set('notes')(e.target.value || undefined)}
                    />
                  )}
                </Field>
              </div>

              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('teams.save')}
                </Button>
                <Button
                  type="button"
                  variant="quiet"
                  onClick={() => {
                    setOpen(false);
                    setEditing(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                {editing ? (
                  <Button
                    type="button"
                    variant="danger"
                    className="ms-auto"
                    onClick={async () => {
                      if (!confirm(t('teams.archiveConfirm'))) return;
                      await archive.mutateAsync(editing.id);
                      setOpen(false);
                      setEditing(null);
                    }}
                  >
                    {t('teams.archive')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel>
        {teams.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : list.length === 0 ? (
          <EmptyState
            title={t('teams.empty')}
            body={t('teams.emptyBody')}
            action={
              <Button variant="primary" onClick={startCreate}>
                {t('teams.create')}
              </Button>
            }
          />
        ) : (
          <Sheet>
            {list.map((tm) => (
              <SheetRow key={tm.id} onClick={() => startEdit(tm)}>
                <SheetNumber>{tm.playerCount}</SheetNumber>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{tm.name}</span>
                  <span className="text-meta text-ink-faint">
                    {t('teams.players', { count: tm.playerCount })}
                  </span>
                </span>
                {tm.ageCategory ? <Chip muted>{tm.ageCategory}</Chip> : null}
                {tm.baseFormation ? (
                  <span className="tabular font-condensed text-meta font-semibold text-ink-muted">
                    {tm.baseFormation}
                  </span>
                ) : null}
              </SheetRow>
            ))}
          </Sheet>
        )}
      </Panel>
    </>
  );
}
